import React, { useEffect, useMemo, useState } from "react";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { AlertCircle, Eye, EyeOff, X } from "lucide-react";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import {
  profileService,
  LegalEntityHierarchy,
  TeamMemberUserType,
} from "@/services/profileService";

interface Props {
  callerEmail: string;
  legalEntities: LegalEntityHierarchy[];
  defaultLegalEntityId?: string | null;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const USER_TYPE_OPTIONS: { value: TeamMemberUserType; label: string }[] = [
  { value: "SUPPORT_USER", label: "Support User" },
  { value: "BRANCH_MANAGER", label: "Branch Manager" },
];

/**
 * Invite a new user into one of the caller's Legal Entities.
 * The new user receives a Keycloak verification email.
 */
export const AddTeamMemberDialog: React.FC<Props> = ({
  callerEmail,
  legalEntities,
  defaultLegalEntityId,
  open,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState({
    legalEntityId: defaultLegalEntityId ?? legalEntities[0]?.id ?? "",
    branchId: "",
    userType: "SUPPORT_USER" as TeamMemberUserType,
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      const leId = defaultLegalEntityId ?? legalEntities[0]?.id ?? "";
      setForm({
        legalEntityId: leId,
        branchId: branchDefaultForLegalEntity(legalEntities, leId),
        userType: "SUPPORT_USER",
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
      });
      setSubmitAttempted(false);
    }
  }, [open, defaultLegalEntityId, legalEntities]);

  const { onOverlayClick } = useModalDismiss(open, onClose);

  const allLegalEntities = useMemo(() => flattenEntities(legalEntities), [legalEntities]);
  const selectedEntity = useMemo(
    () => findEntityById(legalEntities, form.legalEntityId),
    [legalEntities, form.legalEntityId],
  );
  const branchesForLegalEntity = selectedEntity?.branches ?? [];

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);

    const nameMissing = !form.name.trim();
    const emailMissing = !form.email.trim();
    const passwordMissing = !form.password;
    const confirmMissing = !form.confirmPassword;
    const passwordsMismatch = form.password !== form.confirmPassword;
    const legalEntityMissing = !form.legalEntityId;

    if (
      legalEntityMissing ||
      nameMissing ||
      emailMissing ||
      passwordMissing ||
      confirmMissing ||
      passwordsMismatch
    ) {
      return;
    }

    setSubmitting(true);
    try {
      await profileService.addTeamMember(callerEmail, {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        legalEntityId: form.legalEntityId,
        userType: form.userType,
        ...(form.branchId ? { branchId: form.branchId } : {}),
      });
      onCreated();
      onClose();
    } catch (err: any) {
      showActionErrorFromUnknown(err, "Failed to add team member");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-5 pt-20 pb-10 sm:pt-28 sm:pb-14 bg-black/40 backdrop-blur-sm"
      onClick={onOverlayClick}
    >
      <div className="relative flex max-h-[calc(100dvh-7.5rem)] w-full max-w-md flex-col overflow-hidden bg-white rounded-2xl shadow-2xl border border-bridge-border/60 sm:max-h-[calc(100dvh-10.5rem)]">
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-bridge-border/60">
          <div>
            <h2 className="text-lg font-bold text-bridge-fg">Add Team Member</h2>
            <p className="text-xs text-bridge-muted mt-0.5">
              The new user will receive an email-verification link.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-bridge-muted hover:text-bridge-fg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto p-6">

          <div>
            <MenuSelect
              label="Legal Entity"
              required
              value={form.legalEntityId}
              onChange={(v) =>
                setForm({
                  ...form,
                  legalEntityId: v,
                  branchId: branchDefaultForLegalEntity(legalEntities, v),
                })
              }
              placeholder={legalEntities.length === 0 ? "No legal entities available" : "Choose legal entity"}
              disabled={legalEntities.length === 0}
              options={allLegalEntities.map((le) => ({
                value: le.id,
                label: le.name,
                depth: le.depth,
              }))}
            />
            <FieldValidationError
              show={submitAttempted && !form.legalEntityId}
              message="Pick a legal entity"
            />
          </div>

          <MenuSelect
            label="Branch"
            value={form.branchId}
            onChange={(v) => setForm({ ...form, branchId: v })}
            placeholder={
              branchesForLegalEntity.length === 0
                ? "No branches under this entity"
                : "Choose branch"
            }
            disabled={branchesForLegalEntity.length === 0}
            options={branchesForLegalEntity.map((b) => ({
              value: b.id,
              label: b.name,
            }))}
          />

          <MenuSelect
            label="User Type"
            required
            value={form.userType}
            onChange={(v) => setForm({ ...form, userType: v as TeamMemberUserType })}
            placeholder="Choose user type"
            options={USER_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />

          <Field
            label="Name"
            type="text"
            placeholder="Enter full name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
            error={submitAttempted && !form.name.trim() ? "Name is required" : undefined}
          />
          <Field
            label="Email"
            type="email"
            placeholder="Enter email address"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
            required
            error={submitAttempted && !form.email.trim() ? "Email is required" : undefined}
          />
          <Field
            label="Password"
            type="password"
            placeholder="Enter password"
            value={form.password}
            onChange={(v) => setForm({ ...form, password: v })}
            required
            error={submitAttempted && !form.password ? "Password is required" : undefined}
          />
          <Field
            label="Confirm Password"
            type="password"
            placeholder="Re-enter password"
            value={form.confirmPassword}
            onChange={(v) => setForm({ ...form, confirmPassword: v })}
            required
            error={
              submitAttempted
                ? !form.confirmPassword
                  ? "Confirm password is required"
                  : form.password !== form.confirmPassword
                    ? "Passwords do not match"
                    : undefined
                : undefined
            }
          />

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-bridge-muted hover:text-bridge-fg bg-transparent hover:bg-white/5 border border-bridge-border/70 rounded-xl transition-all">
              Cancel
            </button>
            <button type="submit" disabled={submitting || legalEntities.length === 0}
              className="px-5 py-2 text-sm font-bold text-white bg-[#009BF5] hover:bg-[#0085d4] rounded-xl shadow disabled:opacity-50 transition-colors">
              {submitting ? "Inviting..." : "Add Team Member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}> = ({ label, value, onChange, type = "text", placeholder, required, error }) => {
  const isPasswordField = type === "password";
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div>
      <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>
      <div className="relative">
        <input
          type={isPasswordField && showPassword ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full py-2.5 pl-4 bg-white border border-bridge-border/80 rounded-full text-sm text-bridge-fg placeholder:text-bridge-subtle shadow-sm focus:ring-2 focus:ring-bridge-accent/25 focus:border-bridge-accent outline-none transition-all ${isPasswordField ? "pr-11" : "pr-4"}`}
        />
        {isPasswordField && (
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-3 flex items-center text-bridge-muted hover:text-bridge-fg transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      <FieldValidationError show={!!error} message={error ?? ""} />
    </div>
  );
};

function FieldValidationError({ show, message }: { show: boolean; message: string }) {
  if (!show) return null;
  return (
    <div className="mt-1 flex animate-in items-center gap-1.5 text-xs font-medium text-red-500 fade-in slide-in-from-top-1 duration-200">
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function flattenEntities(entities: LegalEntityHierarchy[], depth = 0): (LegalEntityHierarchy & { depth: number })[] {
  return entities.reduce((acc, le) => {
    acc.push({ ...le, depth });
    if (le.subEntities && le.subEntities.length > 0) {
      acc.push(...flattenEntities(le.subEntities, depth + 1));
    }
    return acc;
  }, [] as (LegalEntityHierarchy & { depth: number })[]);
}

function findEntityById(entities: LegalEntityHierarchy[], id: string): LegalEntityHierarchy | null {
  if (!id) return null;
  for (const le of entities) {
    if (le.id === id) return le;
    const found = findEntityById(le.subEntities ?? [], id);
    if (found) return found;
  }
  return null;
}

function branchDefaultForLegalEntity(entities: LegalEntityHierarchy[], legalEntityId: string): string {
  const ent = findEntityById(entities, legalEntityId);
  const bs = ent?.branches ?? [];
  return bs.length === 1 ? bs[0].id : "";
}
